type Status = 'shipped' | 'next' | 'planned';

interface Milestone {
  version: string;
  title: string;
  status: Status;
  summary: string;
  highlights: string[];
}

const MILESTONES: Milestone[] = [
  {
    version: 'v13.0.0',
    title: 'Cognitive Maintenance',
    status: 'shipped',
    summary: 'Graph quality becomes an active architectural responsibility across scanning, enrichment, Architect reasoning, datastore discovery, dreaming, and Explorer inspection.',
    highlights: [
      'Mandatory graph-wide multi-hop semantic enrichment with confidence-aware neighborhood caching',
      'Architect graph-health diagnostics and approved smallest-first self-healing actions',
      'Targeted dream stabilization, PostgreSQL ownership discovery, and structured Explorer metadata',
    ],
  },
  {
    version: 'v12.0.0',
    title: 'Hippodamus',
    status: 'shipped',
    summary: 'The browser-based Architect became the primary standalone working surface. The editor-integrated chat remains available as the VS Code architect.',
    highlights: [
      'Project chat and selected-plan chat from the daemon-served browser surface',
      'Visible adapter, provider, model, route, autonomy state, pass state, and session identity',
      'Auditable DreamGraph MCP tool traces for repository work',
    ],
  },
  {
    version: 'v9.0.0 - v10.6.0',
    title: 'Platform foundations',
    status: 'shipped',
    summary: 'The plugin SDK, webhook surface, provider routing, datastore grounding, and graph-quality work that the v12 browser experience builds on.',
    highlights: [
      '@dreamgraph/sdk and @dreamgraph/host plugin platform',
      'Provider support for Anthropic, OpenAI, Ollama, and LM Studio',
      'Multi-repo and datastore-aware project understanding',
    ],
  },
  {
    version: 'Next',
    title: 'Make the first hour simpler',
    status: 'next',
    summary: 'Keep reducing setup friction and make browser-first onboarding obvious for people who do not want to learn internal terminology first.',
    highlights: [
      'More guided first-run checks in Dashboard and Architect',
      'Clearer multi-repo setup and provider selection',
      'More task-first examples for common project types',
    ],
  },
  {
    version: 'Planned',
    title: 'Broader integrations and hardening',
    status: 'planned',
    summary: 'Continue improving the browser workflow, plugin safety, and project coverage without making the basic path more complicated.',
    highlights: [
      'Architect ergonomics for longer autonomous work',
      'Additional datastore and integration coverage',
      'Continued SDK and runtime safety hardening',
    ],
  },
];

const STATUS_LABEL: Record<Status, string> = {
  shipped: 'Shipped',
  next: 'Next',
  planned: 'Planned',
};

export function Roadmap() {
  return (
    <div className="page">
      <header className="page__header">
        <p className="eyebrow">Roadmap</p>
        <h1>Keep the first hour simple. Keep the system capable.</h1>
        <p className="lede">
          DreamGraph v12 is browser-first: Architect is the main workspace, Explorer is the visual
          map, and Dashboard handles setup and health. The roadmap prioritizes a shorter path from
          install to useful work while continuing platform hardening underneath.
        </p>
      </header>

      <ol className="roadmap">
        {MILESTONES.map((milestone) => (
          <li key={`${milestone.version}-${milestone.title}`} className={`roadmap__slice roadmap__slice--${milestone.status}`}>
            <div className="roadmap__head">
              <span className="roadmap__version">{milestone.version}</span>
              <span className={`roadmap__status roadmap__status--${milestone.status}`}>
                {STATUS_LABEL[milestone.status]}
              </span>
            </div>
            <h2>{milestone.title}</h2>
            <p className="roadmap__why">{milestone.summary}</p>
            <ul className="roadmap__highlights">
              {milestone.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}

